import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageBackground from "@/components/PageBackground";
import MobileBackButton from "@/components/MobileBackButton";
import { useCart } from "@/contexts/CartContext.jsx";
import { useAuth } from "@/AuthContext";
import { db } from "@/lib/firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import useVisibilityRefresh from "@/hooks/useVisibilityRefresh";
import { getStoresByType, STORE_TYPES } from "@/lib/storeManagement";

function peso(n) {
  return Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
}

export default function Cart() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { storeCarts, setQty, removeItem, clear, getStoreItems, getStoreKeys, getStoreTotals } = useCart();
  const [rawQty, setRawQty] = useState({}); // ephemeral typing state per item id
  const [refreshKey, setRefreshKey] = useState(0);
  const [memberStores, setMemberStores] = useState([]);
  const [loading, setLoading] = useState(true);

  // Trigger a light refresh when tab regains focus; here we just bump key
  useVisibilityRefresh(() => setRefreshKey((k) => k + 1), { cooldownMs: 4000 });

  // Load member stores for display names
  useEffect(() => {
    const loadMemberStores = async () => {
      try {
        const result = await getStoresByType(STORE_TYPES.MEMBER);
        if (result.success) {
          setMemberStores(result.stores);
        }
      } catch (error) {
        console.error('Error loading member stores:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMemberStores();
  }, []);

  // Helper function to get store display name
  function getStoreDisplayName(storeKey) {
    if (storeKey === 'cooperative') {
      return 'Cooperative Store';
    } else if (storeKey === 'legacy') {
      return 'Legacy Items';
    } else if (storeKey.startsWith('member_')) {
      const storeId = storeKey.replace('member_', '');
      const store = memberStores.find(s => s.id === storeId);
      return store ? store.name : `Member Store (${storeId})`;
    } else if (storeKey.startsWith('establishment_')) {
      const storeId = storeKey.replace('establishment_', '');
      return `Establishment Store (${storeId})`;
    }
    return storeKey;
  }

  // Optional: persist cart to Firestore when signed in
  useEffect(() => {
    if (!user?.uid || !storeCarts || Object.keys(storeCarts).length === 0) return;
    
    try {
      // Sanitize store carts for Firestore
      const sanitizedStoreCarts = {};
      Object.entries(storeCarts).forEach(([storeKey, items]) => {
        const sanitizedItems = items.map(item => ({
          id: String(item.id || ''),
          name: String(item.name || ''),
          price: Number(item.price || 0),
          qty: Number(item.qty || 0),
          sku: String(item.sku || ''),
          imageUrl: String(item.imageUrl || item.image || ''),
          storeType: String(item.storeType || ''),
          storeId: String(item.storeId || ''),
        })).filter(item => item.id && item.name);
        
        if (sanitizedItems.length > 0) {
          sanitizedStoreCarts[storeKey] = sanitizedItems;
        }
      });
      
      if (Object.keys(sanitizedStoreCarts).length === 0) return;
      
      const cartRef = doc(db, "carts", user.uid);
      setDoc(cartRef, { 
        storeCarts: sanitizedStoreCarts, 
        updatedAt: serverTimestamp() 
      }, { merge: true }).catch(() => {
        // Silent fail - cart persistence is optional
      });
    } catch (error) {
      // Silent fail - cart persistence is optional
    }
  }, [storeCarts, user, refreshKey]);

  const storeKeys = getStoreKeys();
  const empty = storeKeys.length === 0;

  function displayQty(storeKey, it) {
    const v = rawQty[`${storeKey}_${it.id}`];
    return v !== undefined ? v : it.qty;
  }

  function handleQtyChange(storeKey, id, value) {
    // accept only digits and a single dot; limit to two decimals
    if (typeof value !== "string") value = String(value ?? "");
    let s = value.replace(/[^\d.]/g, "");
    const firstDot = s.indexOf(".");
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
    }
    const parts = s.split(".");
    if (parts.length > 1) s = parts[0] + "." + parts[1].slice(0, 2);
    else s = parts[0];
    setRawQty((prev) => ({ ...prev, [`${storeKey}_${id}`]: s }));
  }

  function handleQtyBlur(storeKey, id, value) {
    const items = getStoreItems(storeKey);
    const item = items.find(it => it.id === id);
    const step = item?.quantityStep || 0.25;
    const raw = Number(value || step);
    const q = Math.max(step, Math.round(raw / step) * step);
    setQty(storeKey, id, q);
    setRawQty((prev) => {
      const next = { ...prev };
      delete next[`${storeKey}_${id}`];
      return next;
    });
  }

  return (
    <>
      <MobileBackButton to="/store" />
      <PageBackground
        image="https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?auto=format&fit=crop&w=1500&q=80"
        boxed={false}
        overlayClass="bg-white/85 backdrop-blur"
        edgeToEdge={true}
      >
      <div className="min-h-screen">
        <div className="p-2 sm:p-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">Your Cart</h1>
            <div className="flex items-center gap-2">
              <a
                href="/cart"
                className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border"
                title="Cart"
                aria-label="Cart"
              >
                {/* Cart icon */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-ink">
                  <path d="M2.25 3a.75.75 0 000 1.5h1.386c.34 0 .64.23.72.558l2.2 8.8A1.875 1.875 0 008.37 15h8.505a1.875 1.875 0 001.814-1.35l1.64-5.415a.75.75 0 00-.72-.985H6.855l-.37-1.48A2.25 2.25 0 003.636 3H2.25z" />
                  <path d="M8.25 20.25a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM17.25 20.25a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                </svg>
              </a>
              <Link className="btn btn-outline" to="/store">Continue Shopping</Link>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading cart...</p>
            </div>
          ) : empty ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🛒</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
              <p className="text-gray-600 mb-4">
                Looks like you haven't added any items to your cart yet.
              </p>
              <Link className="btn btn-primary" to="/store">
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {storeKeys.map((storeKey) => {
                const items = getStoreItems(storeKey);
                const totals = getStoreTotals(storeKey);
                const storeDisplayName = getStoreDisplayName(storeKey);
                
                return (
                  <div key={storeKey} className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="mb-4">
                      <div className="flex items-start justify-between mb-2">
                        <h2 className="text-lg font-semibold text-gray-900">{storeDisplayName}</h2>
                        <button 
                          className="btn btn-sm btn-outline text-xs px-2 py-1" 
                          onClick={() => clear(storeKey)}
                        >
                          Clear
                        </button>
                      </div>
                      <div className="text-sm text-gray-600">
                        {items.length} item{items.length !== 1 ? 's' : ''} • ₱{peso(totals.subtotal)}
                      </div>
                    </div>
                    
                    {/* Mobile-first card layout */}
                    <div className="space-y-3 sm:hidden">
                      {items.map((it) => (
                        <div key={it.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              {it.imageUrl || it.image ? (
                                <img 
                                  src={it.imageUrl || it.image} 
                                  alt={it.name} 
                                  className="w-16 h-16 object-cover rounded-lg border"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-16 h-16 bg-gray-200 rounded-lg border flex items-center justify-center">
                                  <span className="text-gray-400 text-xs">No Image</span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-900 text-sm leading-tight mb-1">{it.name}</h3>
                              {it.sku && <p className="text-xs text-gray-500 mb-2">SKU: {it.sku}</p>}
                              <div className="text-sm font-semibold text-green-600 mb-3">₱{peso(it.price)} each</div>
                              
                              {/* Quantity controls */}
                              <div className="flex items-center gap-3 mb-3">
                                <label className="text-xs text-gray-600">Qty:</label>
                                <div className="flex items-center border border-gray-300 rounded-lg">
                                  <button
                                    type="button"
                                    className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-l-lg"
                                    onClick={() => {
                                      const currentQty = Number(displayQty(storeKey, it));
                                      const newQty = Math.max(it.quantityStep || 0.25, currentQty - (it.quantityStep || 0.25));
                                      handleQtyBlur(storeKey, it.id, newQty);
                                    }}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                    </svg>
                                  </button>
                                  <input
                                    type="number"
                                    className="w-16 h-8 text-center text-sm border-0 focus:ring-0"
                                    value={displayQty(storeKey, it)}
                                    onChange={(e) => handleQtyChange(storeKey, it.id, e.target.value)}
                                    onBlur={(e) => handleQtyBlur(storeKey, it.id, e.target.value)}
                                    step={it.quantityStep || 0.25}
                                    min={it.quantityStep || 0.25}
                                    inputMode="decimal" 
                                    enterKeyHint="done"
                                  />
                                  <button
                                    type="button"
                                    className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-r-lg"
                                    onClick={() => {
                                      const currentQty = Number(displayQty(storeKey, it));
                                      const newQty = currentQty + (it.quantityStep || 0.25);
                                      handleQtyBlur(storeKey, it.id, newQty);
                                    }}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                              
                              {/* Item total and remove button */}
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-semibold text-gray-900">
                                  Total: ₱{peso(Number(it.price || 0) * Number(it.qty || 0))}
                                </div>
                                <button 
                                  className="text-red-600 hover:text-red-700 text-sm font-medium px-3 py-1 rounded-lg hover:bg-red-50 transition-colors"
                                  onClick={() => removeItem(storeKey, it.id)}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop table layout */}
                    <div className="hidden sm:block overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="p-3 text-left border border-gray-300">Product</th>
                            <th className="p-3 text-left border border-gray-300">Price</th>
                            <th className="p-3 text-left border border-gray-300">Quantity</th>
                            <th className="p-3 text-left border border-gray-300">Total</th>
                            <th className="p-3 text-left border border-gray-300">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it) => (
                            <tr key={it.id} className="hover:bg-gray-50">
                              <td className="p-3 border border-gray-300">
                                <div className="flex items-center gap-3">
                                  {it.imageUrl || it.image ? (
                                    <img 
                                      src={it.imageUrl || it.image} 
                                      alt={it.name} 
                                      className="w-12 h-12 object-cover rounded border"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  ) : (
                                    <div className="w-12 h-12 bg-gray-200 rounded border flex items-center justify-center">
                                      <span className="text-gray-400 text-xs">No Image</span>
                                    </div>
                                  )}
                                  <div>
                                    <div className="font-medium text-gray-900">{it.name}</div>
                                    {it.sku && <div className="text-sm text-gray-500">SKU: {it.sku}</div>}
                                  </div>
                                </div>
                              </td>
                              <td className="p-3 font-mono">₱{peso(it.price)}</td>
                              <td className="p-3">
                                <input
                                  type="number"
                                  className="input w-20"
                                  value={displayQty(storeKey, it)}
                                  onChange={(e) => handleQtyChange(storeKey, it.id, e.target.value)}
                                  onBlur={(e) => handleQtyBlur(storeKey, it.id, e.target.value)}
                                  step={it.quantityStep || 0.25}
                                  min={it.quantityStep || 0.25}
                                  inputMode="decimal" 
                                  enterKeyHint="done"
                                />
                              </td>
                              <td className="p-3 text-right font-mono">
                                ₱{peso(Number(it.price || 0) * Number(it.qty || 0))}
                              </td>
                              <td className="p-3 text-right">
                                <button 
                                  className="btn btn-sm btn-outline" 
                                  onClick={() => removeItem(storeKey, it.id)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="border-t">
                          <tr>
                            <td className="p-3" colSpan={3}></td>
                            <td className="p-3 text-right font-semibold">Store Total: ₱{peso(totals.subtotal)}</td>
                            <td className="p-3"></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    
                    <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:justify-end">
                      <button 
                        className="btn btn-primary w-full sm:w-auto py-3 text-base font-medium" 
                        onClick={() => nav(`/checkout?store=${storeKey}`)}
                      >
                        Checkout from {storeDisplayName}
                      </button>
                    </div>
                  </div>
                );
              })}
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">All Stores Summary</h3>
                    <p className="text-sm text-gray-600">
                      {storeKeys.length} store{storeKeys.length !== 1 ? 's' : ''} with items
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">
                      ₱{peso(storeKeys.reduce((total, storeKey) => total + getStoreTotals(storeKey).subtotal, 0))}
                    </div>
                    <div className="text-sm text-gray-600">Total across all stores</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PageBackground>
    </>
  );
}